import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  PersonDetailsDocument,
  PersonDetailsQuery,
  usePersonUpdateVisibleToMutation,
} from "gql/generated";
import { OperatorType } from "./Listings";
import { useLoggedInAs, usePostHog } from "shared/hooks";

/**
 * Find the matching operator from an override's operators array
 * @param overrides Array of all overrides
 * @param operatorId The operator's _entityId
 * @returns The specific operator object from the operators array or undefined if none found
 */
const findOperatorOverrideForOrganization = (
  listing: OperatorType,
  organizationId: string,
):
  | {
      operatorEntityId: string;
      title?: string | null | undefined;
    }
  | undefined => {
  if (!listing || !organizationId) return undefined;

  const override = listing.organizations?.find(
    override => override?._id === organizationId,
  );
  return {
    operatorEntityId: listing._entityId,
    title: override?.title ?? "",
  };
};

export const useListingCard = (
  allListings:
    | NonNullable<PersonDetailsQuery["personDetails"]>["operators"]
    | undefined
    | null,
  operatorListing: OperatorType,
  personId: string,
  operatorId: string,
  organizationId: string,
) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.LISTINGS.LISTING_CARD",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const loggedInAs = useLoggedInAs();
  const { trackPostHogException } = usePostHog();

  const [updatePersonVisibleTo] = usePersonUpdateVisibleToMutation({
    refetchQueries: [PersonDetailsDocument],
  });

  // Find the specific operator override for this organization
  const [operatorOverride, setOperatorOverride] = useState(
    findOperatorOverrideForOrganization(operatorListing, organizationId),
  );

  // Initialize with the title from the override if available
  const [value, setValue] = useState(operatorOverride?.title || "");

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
  };

  const handleValueCommit = async () => {
    if (
      !personId ||
      !organizationId ||
      value === operatorOverride?.title ||
      !allListings
    )
      return;

    try {
      /**
       * ONLY organizations can update the titles
       * - The allListings array will be flat (one org per operator),
       *   but it is still an array, so we need to find the org by its ID
       *
       * Adminland users have view-only access to this card, so this
       * will never be called for them
       */
      // Update the allListings array with the new title
      for (let i = 0; i < allListings.length; i++) {
        const listing = allListings[i];
        if (!listing) continue;

        const org = listing.organizations?.find(
          org => org?._id === organizationId,
        );
        const title =
          org?._id === organizationId && listing._entityId === operatorId
            ? value
            : (org?.title ?? "");

        allListings[i] = {
          ...listing,
          organizations: listing.organizations?.map(org => {
            if (org?._id === organizationId) {
              return { ...org, title };
            }
            return org;
          }),
        };

        if (listing._entityId === operatorId) {
          operatorListing = allListings[i] as OperatorType;
        }
      }

      await updatePersonVisibleTo({
        variables: {
          id: personId,
          entityId: loggedInAs?.entityId,
          type: loggedInAs?.type as EnumOwnerType,
          metadata: {
            operatorOverrides: allListings
              ?.filter(listing => !!listing)
              .map(listing => {
                const org = listing.organizations?.find(
                  org => org?._id === organizationId,
                );
                const title =
                  org?._id === organizationId &&
                  listing._entityId === operatorId
                    ? value
                    : (org?.title ?? "");
                return {
                  operatorEntityId: listing._entityId,
                  title,
                };
              }),
          },
        },
      }).then(res => {
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("MESSAGES.SUCCESS", {
            person: `${res.data?.personUpdateVisibleTo.personInfo?.firstName} ${res.data?.personUpdateVisibleTo.personInfo?.lastName}`,
          }),
        });
        const override = findOperatorOverrideForOrganization(
          operatorListing,
          organizationId,
        );
        setValue(override?.title || "");
        setOperatorOverride(override);
      });
    } catch {
      trackPostHogException(new Error("Error updating listing card"), {
        fileName: "useListingCard.ts",
        method: "handleValueCommit",
        message: "Error updating listing card",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("MESSAGES.ERROR"),
      });
    }
  };

  return {
    value,
    handleValueChange,
    handleValueCommit,
    t,
    isAdminland: loggedInAs?.type === EnumOwnerType.TnbInternal,
  };
};
